import React from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { TrendingUp, AlertTriangle } from 'lucide-react';
import { useTranslation } from '../../hooks/useTranslation';

interface ListFetchStatesProps {
  isLoading: boolean;
  isError: boolean;
  onRetry?: () => void;
  loadingMessage?: string;
  errorMessage?: string;
  retryButtonText?: string;
}

/**
 * Common component for loading and error states that matches OrdersReport pattern
 *
 * Loading state: Shows a card with centered loading indicator
 * Error state: Shows a card with error message and retry button
 */
export const ListFetchStates: React.FC<ListFetchStatesProps> = ({
  isLoading,
  isError,
  onRetry,
  loadingMessage,
  errorMessage,
  retryButtonText,
}) => {
  const { translate } = useTranslation();

  // Loading State - matches OrdersReport pattern exactly
  if (isLoading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-12">
          <div className="text-center space-y-4">
            <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center mx-auto">
              <TrendingUp className="h-6 w-6 text-primary animate-pulse" />
            </div>
            <div>
              <h3 className="font-semibold">
                {loadingMessage || translate('common.loading', 'Loading...')}
              </h3>
              <p className="text-muted-foreground">
                {translate('common.fetching_data', 'Fetching data...')}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Error State - matches OrdersReport pattern exactly
  if (isError) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-12">
          <div className="text-center space-y-4">
            <div className="h-12 w-12 rounded-full bg-destructive/10 flex items-center justify-center mx-auto">
              <AlertTriangle className="h-6 w-6 text-destructive" />
            </div>
            <div>
              <h3 className="font-semibold">
                {errorMessage ||
                  translate('common.failed_to_load', 'Failed to load data')}
              </h3>
              <p className="text-muted-foreground">
                {translate(
                  'common.try_again_message',
                  'Please try again or contact support if the problem persists.'
                )}
              </p>
            </div>
            {onRetry && (
              <Button onClick={onRetry} variant="outline">
                {retryButtonText || translate('common.try_again', 'Try Again')}
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    );
  }

  return null;
};
